import clsx from 'clsx';
import { motion } from 'framer-motion';
import React, { FC, useEffect, useRef, useState } from 'react';
import AnchorLink from 'react-anchor-link-smooth-scroll';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import {
  VisibilitySection,
  useVisibleSection,
} from '@provider-directory/hooks/useVisibleSection';
import { centerItemInContainer } from './animation-scroll';

const MOBILE_OFFSET = 80;
const SCROLL_DETECT_DELAY = 700;
const SCROLL_ANIMATION_DURATION = 300;

export interface SideMenuProps {
  menuItems: VisibilitySection[];
  className?: string;
  onItemClick?: (section: VisibilitySection) => void;
}

function scrollToMenuItemById(
  id: string,
  menuItems: VisibilitySection[],
  itemRefs: React.RefObject<HTMLLIElement[]>,
  containerRef: React.RefObject<HTMLUListElement | null>,
) {
  const index = menuItems.findIndex((item) => item.id === id);
  if (index === -1) return;

  const containerEl = containerRef.current;
  const itemEl = itemRefs.current[index];
  if (!containerEl || !itemEl) return;

  centerItemInContainer(containerEl, itemEl, SCROLL_ANIMATION_DURATION);
}

const SideMenu: FC<SideMenuProps> = ({ menuItems, className, onItemClick }) => {
  const isDesktop = useIsDesktop();
  const offset = isDesktop ? 0 : MOBILE_OFFSET;
  const { t } = useTranslation();

  const { visibleSectionId, pauseTracking, resumeTracking } = useVisibleSection(
    menuItems,
    offset,
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const itemRefs = useRef<HTMLLIElement[]>([]);

  const containerRef = useRef<HTMLUListElement>(null);
  const activeId = selectedId ?? visibleSectionId;

  const handleClick = (section: VisibilitySection) => {
    const { id } = section;
    scrollToMenuItemById(id, menuItems, itemRefs, containerRef);

    setSelectedId(id);
    pauseTracking();
    onItemClick?.(section);

    setTimeout(() => {
      setSelectedId(null);
      resumeTracking();
    }, SCROLL_DETECT_DELAY);
  };

  useEffect(() => {
    if (!visibleSectionId || isDesktop) return;
    scrollToMenuItemById(visibleSectionId, menuItems, itemRefs, containerRef);
  }, [visibleSectionId, isDesktop, menuItems]);

  return (
    <nav aria-label={t('aria.providerDetailsSections')}>
      <ul
        ref={containerRef}
        className={clsx(
          'no-scrollbar relative flex flex-row gap-4 overflow-x-auto text-gray-500',
          className,
        )}
      >
        {menuItems.map((section, index) => {
          const { id, title } = section;
          const isActive = activeId === id;

          return (
            <li
              key={id}
              ref={(el) => {
                if (el) itemRefs.current[index] = el;
              }}
              className={clsx(
                'relative text-body-medium-semibold mobile:pb-3 desktop:pb-0',
                isActive && 'text-neutral-950',
              )}
            >
              <AnchorLink
                offset={() => offset}
                href={`#${id}`}
                onClick={() => {
                  handleClick(section);
                }}
              >
                {title}
              </AnchorLink>

              {!isDesktop && isActive && (
                <motion.div
                  layoutId={'activeIndicator'}
                  className={
                    'absolute bottom-0 left-0 h-[4px] w-full bg-neutral-950'
                  }
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                />
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default SideMenu;
